import { ApolloError } from '@apollo/client';
import { format } from 'date-fns';

import { SIMULATION } from '~queries';
import { GetSimulationQuery, GetSimulationQueryVariables, Simulation } from '~types/appsync.types';
import { Participant } from '~types/participant';
import { convertParticipantToParticipantInput, stripTypename } from '~utils';

import useApiGatewayQuery from '../useApiGatewayQuery/useApiGatewayQuery';

interface UseSimulationParams {
  catalogSelectable: boolean;
  contractorId: number | undefined;
  date: Date | undefined;
  forceSkip: boolean;
  participants: Participant[] | undefined;
  productSlug: string | null | undefined;
  productCategoryShortname: string | null | undefined;
  productCategoryVisible: boolean;
  validityCategoryShortname: string | null | undefined;
  validityCategoryVisible: boolean;
}

const useSimulation = ({
  catalogSelectable,
  contractorId,
  date,
  forceSkip,
  participants,
  productSlug,
  productCategoryShortname,
  productCategoryVisible,
  validityCategoryShortname,
  validityCategoryVisible,
}: UseSimulationParams): [
  Simulation | undefined,
  Simulation | undefined,
  boolean,
  ApolloError | undefined,
] => {
  const skip =
    forceSkip ||
    !contractorId ||
    !date ||
    !participants?.length ||
    !productSlug ||
    !productCategoryShortname ||
    !validityCategoryShortname;

  const formattedDate = date ? format(date, 'yyyy-MM-dd') : '';

  const { loading, error, data, previousData } = useApiGatewayQuery<
    GetSimulationQuery,
    GetSimulationQueryVariables
  >(SIMULATION, {
    skip,
    variables: {
      catalogSelectable,
      contractorId: contractorId!,
      date: formattedDate,
      participants: participants?.map((p) =>
        convertParticipantToParticipantInput(
          { ...p, consumerCategory: stripTypename(p.consumerCategory) },
          productSlug!,
          formattedDate,
        ),
      ),
      productCategoryShortname: productCategoryShortname!,
      productCategoryVisible,
      validityCategoryShortname: validityCategoryShortname!,
      validityCategoryVisible,
    },
  });

  const isQueryCompleted = !skip && !loading && !error;
  const simulationError =
    isQueryCompleted && data?.simulation === null
      ? new ApolloError({ errorMessage: 'Simulation returned null' })
      : error;

  return [
    data?.simulation ?? undefined,
    previousData?.simulation ?? undefined,
    loading,
    simulationError,
  ];
};

export default useSimulation;
